import * as React from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { BeatLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import { UnscrollableContainer } from '../../../../../misc';
import { ADD_NEW_STAFFS } from './graphql';
import {
  Clear,
  InputErrMess,
  InputWrap,
  Label,
  SelectInput,
  SelectOption,
  Submit,
} from '../addStudent/styles';
import { Input } from './styles';
import { H2 } from '../signInvigilatorRecords/styles';
import { AddNewStaffMutation } from '../../../../../generated/graphql';

type StaffInList = {
  id: number;
  name: string;
  bankName?: string;
  bankBranch?: string;
  bankAccNumber?: string;
};

interface FormikValues {
  userType: string;
  category: string;
  hoursWorked: number | '';
  minutesWorked: number | '';
  numOfSessionsDone: string;
  role: string;
}

export const AddNewStaff = (): JSX.Element => {
  const [showBankForm, setShowBankForm] = React.useState(true);
  const [staffInList, setStaffInList] = React.useState<StaffInList[]>([]);
  const [staffNamesWithBank, setStaffNamesWithBank] = React.useState({
    name: '',
    bankName: '',
    bankBranch: '',
    bankAccNumber: '',
  });
  const [showStaffInListError, setShowStaffInListError] = React.useState(false);
  const [showNameInputError, setShowNameInputError] = React.useState(false);
  const [showBankDetailsError, setShowBankDetailsError] = React.useState(false);
  const [addNewStaffs, { loading, error, data }] = useMutation<AddNewStaffMutation>(ADD_NEW_STAFFS);
  const firstUpdate = React.useRef(true);
  const {
    handleChange,
    handleSubmit,
    handleReset,
    values,
    errors,
    handleBlur,
  }: FormikProps<FormikValues> = useFormik<FormikValues>({
    initialValues: {
      userType: '',
      category: '',
      hoursWorked: '',
      minutesWorked: '',
      numOfSessionsDone: '',
      role: '',
    },
    onSubmit: async (formValues) => {
      if (staffInList.length === 0) {
        setShowStaffInListError(true);
        return;
      }

      const staffListToSubmit = staffInList.map((staff) => {
        return {
          name: staff.name,
          category: formValues.category,
          hours: (formValues.hoursWorked as number) + (formValues.minutesWorked as number) / 60,
          role: formValues.role ? formValues.role : null,
          userType: formValues.userType,
          numOfSessionDone: formValues.numOfSessionsDone,
          bankAccNumber: staff.bankAccNumber ? staff.bankAccNumber : null,
          bankName: staff.bankName ? staff.bankName : null,
          bankBranch: staff.bankBranch ? staff.bankBranch : null,
        };
      });
      await addNewStaffs({ variables: { staffsDetails: staffListToSubmit } });
    },
    validationSchema: Yup.object().shape({
      userType: Yup.string().required('Staff type must be provided'),
      category: Yup.string().required('Staff category must be provided'),
      hoursWorked: Yup.string().required('Staff hours worked must be provided'),
      numOfSessionsDone: Yup.string().required('Staff number of session done must be provided'),
    }),
  });

  // Effect to show error when some occurs
  React.useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (error) {
      toast.error(error.message);
      return;
    } else if (data?.addNewStaff === 'Success') {
      toast.success('Successfully added new staff data!');
    }
  }, [data, error]);

  const addToList = (staff: StaffInList): void => {
    let showSameNameWarning = false;
    if (staff.name === '') {
      setShowNameInputError(true);
      return;
    } else {
      setShowNameInputError(false);
    }

    if (
      (staff.bankBranch && !staff.bankAccNumber) ||
      (staff.bankName && !staff.bankAccNumber) ||
      (staff.bankAccNumber && !staff.bankName)
    ) {
      setShowBankForm(true);
      setShowBankDetailsError(true);
      return;
    } else {
      setShowBankDetailsError(false);
    }

    for (const _staff of staffInList) {
      if (_staff.name === staff.name) {
        showSameNameWarning = true;
      }
      if (
        _staff.name === staff.name &&
        staff.bankAccNumber !== '' &&
        _staff.bankAccNumber === staff.bankAccNumber
      ) {
        toast.error(`staff with name ${_staff.name} has the same account number!`);
        return;
      }
    }

    if (showSameNameWarning) toast.warn('Name already exist!');

    setStaffInList([...staffInList, staff]);
  };

  const removeFromList = (id: number): void => {
    const filtered = staffInList.filter((staff) => staff.id !== id);
    setStaffInList(filtered);
  };

  return (
    <UnscrollableContainer>
      <div>
        <H2 className="text-blue-700 2xl:my-12 my-8">New Staff</H2>
        <div
          className="bg-slate-100  pb-30 rounded-md
        p-8 2xl:p-12 w-10/12 lg:h-[37rem] 2xl:h-[42rem] overflow-auto"
        >
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="flex flex-col gap-3">
              <InputWrap>
                <Label>Staff Name</Label>
                <Input
                  name="name"
                  type="text"
                  onChange={(e) => {
                    if (e.target.value === '') setShowNameInputError(!showNameInputError);
                    setStaffNamesWithBank({ ...staffNamesWithBank, name: e.target.value });
                  }}
                  value={staffNamesWithBank.name}
                  placeholder="Enter staff name"
                />
                {showNameInputError && (
                  <InputErrMess>Please staff&apos;s name must be provided</InputErrMess>
                )}
              </InputWrap>
              <p
                className="font-semibold text-blue-600 cursor-pointer"
                onClick={() => setShowBankForm(!showBankForm)}
              >
                {showBankForm ? 'Close bank form' : 'Add bank details'}
              </p>
              {showBankForm && (
                <>
                  <InputWrap>
                    <Label>Bank Acc. Number</Label>
                    <Input
                      name="bankAccNumber"
                      type="text"
                      onChange={(e) =>
                        setStaffNamesWithBank({
                          ...staffNamesWithBank,
                          bankAccNumber: e.target.value,
                        })
                      }
                      value={staffNamesWithBank.bankAccNumber}
                      placeholder="Enter staff's bank account number"
                    />
                  </InputWrap>
                  <InputWrap>
                    <Label>Bank Name</Label>
                    <Input
                      name="bankName"
                      type="text"
                      onChange={(e) =>
                        setStaffNamesWithBank({ ...staffNamesWithBank, bankName: e.target.value })
                      }
                      value={staffNamesWithBank.bankName}
                      placeholder="Enter staff's bank name"
                    />
                  </InputWrap>
                  <InputWrap>
                    <Label>Bank Branch</Label>
                    <Input
                      name="bankBranch"
                      type="text"
                      onChange={(e) =>
                        setStaffNamesWithBank({
                          ...staffNamesWithBank,
                          bankBranch: e.target.value,
                        })
                      }
                      value={staffNamesWithBank.bankBranch}
                      placeholder="Enter staff's bank branch"
                    />
                    {showBankDetailsError && (
                      <InputErrMess>Please provide the rest of the bank details</InputErrMess>
                    )}
                  </InputWrap>
                </>
              )}
              <button
                className="py-2 w-5/12 px-6 mt-4 bg-lime-500
           rounded-sm text-white font-semibold"
                onClick={() => {
                  addToList({
                    id: Date.now() + Math.random(),
                    name: staffNamesWithBank.name,
                    bankAccNumber: staffNamesWithBank.bankAccNumber,
                    bankBranch: staffNamesWithBank.bankBranch,
                    bankName: staffNamesWithBank.bankName,
                  });
                  setStaffNamesWithBank({
                    name: '',
                    bankAccNumber: '',
                    bankName: '',
                    bankBranch: '',
                  });
                }}
              >
                Add to list
              </button>
            </div>

            <div className="bg-slate-200 h-64">
              <h4 className="font-bold p-5">List of names added</h4>
              <div className="h-48 overflow-auto pb-6 px-4">
                {staffInList.length !== 0 ? (
                  staffInList.map((staff, index: number) => (
                    <div className="flex gap-3 items-center" key={index}>
                      <div
                        className="px-4 py-2 flex flex-col 
                      rounded-md bg-emerald-100 flex-1 font-medium my-2"
                      >
                        <span>
                          {' '}
                          name: <b>{staff.name}</b>
                        </span>
                        {staff.bankAccNumber && (
                          <span>
                            Acc no: <b>{staff.bankAccNumber}</b>
                          </span>
                        )}
                      </div>
                      <i
                        onClick={() => removeFromList(staff.id)}
                        className="fas fa-trash-alt text-2xl cursor-pointer
                         text-red-500 hover:text-red-600"
                      />
                    </div>
                  ))
                ) : (
                  <div className="text-center">
                    <p>You have not select any course for student yet!</p>
                    {showStaffInListError && (
                      <p className="text-red-600">* Please you must select at least one course!</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-6">
              <InputWrap>
                <Label>Staff Type</Label>
                <SelectInput
                  name="userType"
                  onChange={handleChange}
                  value={values.userType}
                  className="rounded-md pr-6"
                  onBlur={handleBlur}
                >
                  <SelectOption value="">Select staff type</SelectOption>
                  <SelectOption value="proctor">Proctor</SelectOption>
                  <SelectOption value="attendant">Attendant</SelectOption>
                  <SelectOption value="nssPersonnel">NSS Personnel</SelectOption>
                </SelectInput>
                <InputErrMess>{errors.userType}</InputErrMess>
              </InputWrap>
              <InputWrap>
                <Label>Category</Label>
                <Input
                  value={values.category}
                  onChange={handleChange}
                  type="text"
                  name="category"
                  onBlur={handleBlur}
                  placeholder="Enter staff(s) category eg:NSM/NSS-Pro-off"
                />
                <InputErrMess>{errors.category}</InputErrMess>
              </InputWrap>
              <InputWrap>
                <Label>Time worked</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    name="hoursWorked"
                    value={values.hoursWorked}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter hours"
                  />
                  <span className="font-bold">:</span>
                  <Input
                    type="number"
                    name="minutesWorked"
                    value={values.minutesWorked}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter minutes"
                  />
                </div>
                <InputErrMess>{errors.hoursWorked}</InputErrMess>
              </InputWrap>
              <InputWrap>
                <Label>Number of Sessions</Label>
                <Input
                  type="number"
                  name="numOfSessionsDone"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.numOfSessionsDone}
                  placeholder="Enter number of session for staff(s)"
                />
                <InputErrMess>{errors.numOfSessionsDone}</InputErrMess>
              </InputWrap>
              <InputWrap>
                <Label>Staff Role</Label>
                <Input
                  value={values.role}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="text"
                  name="role"
                  placeholder="Enter staff(s) role eg:cleaner"
                />
              </InputWrap>
            </div>
            <div className="flex gap-4 2xl:mt-12 mt-8">
              <Submit className="font-bold" type="submit">
                {loading ? <BeatLoader size={14} color="#fff" /> : 'Submit'}
              </Submit>
              <Clear
                onClick={() => {
                  setStaffInList([]);
                  handleReset();
                }}
                type="button"
              >
                Reset
              </Clear>
            </div>
          </form>
        </div>
      </div>
    </UnscrollableContainer>
  );
};
